/**
 * Recommendations renderer
 *
 * On first visit (or after the 24-hour TTL expires): fetches recommended
 * product handles from the backend and caches them in localStorage keyed by
 * Firebase UID.
 *
 * On subsequent visits: reads handles directly from localStorage and renders
 * cards via Shopify's Section Rendering API — no backend call needed.
 *
 * Flow:
 *   1. Find [data-needs-fallback] containers rendered by sections/recommendations.liquid
 *   2. Read handles from localStorage (if present and fresh) or fetch from backend
 *   3. GET /products/{handle}?sections=product-card  (parallel, one per product)
 *   4. Populate the list, reveal it, hide spinner, call initPages()
 */
(function () {
  'use strict';

  var SECTION_NAME = 'product-card';
  var LS_KEY_PREFIX = 'mai-recs-';

  // ---------------------------------------------------------------------------
  // Backend helpers
  // ---------------------------------------------------------------------------

  function getApiBase() {
    return window.API_BASE_URL
      ? window.API_BASE_URL.replace(/\/shopify\/?$/, '')
      : '';
  }

  async function refreshIdToken() {
    var auth = window.firebaseAuth;
    if (!auth) throw new Error('Firebase auth not available');
    var user = await auth.waitForAuthState(3000);
    if (!user) throw new Error('No Firebase user — please log in again');
    var newToken = await user.getIdToken(true);
    sessionStorage.setItem('firebaseIdToken', newToken);
    return newToken;
  }

  async function apiFetch(url, options) {
    options = options || {};
    var token = sessionStorage.getItem('firebaseIdToken');
    var headers = Object.assign(
      { 'Content-Type': 'application/json' },
      options.headers || {},
      token ? { 'Authorization': 'Bearer ' + token } : {}
    );
    var response = await fetch(url, Object.assign({}, options, { headers: headers }));
    if (response.status === 401) {
      try {
        var body = await response.clone().json();
        if (body && body.message && body.message.indexOf('Expired or invalid id token') !== -1) {
          var freshToken = await refreshIdToken();
          headers['Authorization'] = 'Bearer ' + freshToken;
          response = await fetch(url, Object.assign({}, options, { headers: headers }));
        }
      } catch (e) {
        console.error('[recommendations] error calling ' + url, e);
      }
    }
    return response;
  }

  async function fetchRecommendations() {
    var base = getApiBase();
    if (!base) throw new Error('API_BASE_URL not set');
    var response = await apiFetch(base + '/recommendations');
    if (!response.ok) throw new Error('Recommendations fetch failed: ' + response.status);
    return response.json();
  }

  // ---------------------------------------------------------------------------
  // localStorage cache (keyed by Firebase UID)
  // ---------------------------------------------------------------------------

  /**
   * Cache is considered fresh if it was populated today at or after 06:00
   * local time. The recommendation job runs at 04:00 and finishes well before
   * 06:00, so anything cached after that cutoff is up to date.
   */
  function isCacheFresh(ts) {
    var cachedAt = new Date(ts);
    var todayCutoff = new Date();
    todayCutoff.setHours(6, 0, 0, 0); // today at 06:00 local time
    return cachedAt >= todayCutoff;
  }

  function getLocalHandles(uid) {
    try {
      var raw = localStorage.getItem(LS_KEY_PREFIX + uid);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.handles)) return null;
      if (!isCacheFresh(parsed.ts)) return null;
      return parsed.handles;
    } catch {
      return null;
    }
  }

  function setLocalHandles(uid, handles) {
    try {
      localStorage.setItem(
        LS_KEY_PREFIX + uid,
        JSON.stringify({ handles: handles, ts: Date.now() })
      );
    } catch (e) {
      console.error('[recommendations] error setting local handles', e);
    }
  }

  // Last known Firebase UID — lets us skip auth on cache hits
  var LS_UID_KEY = 'mai-recs-uid';

  function getCachedUid() {
    try { return localStorage.getItem(LS_UID_KEY); } catch (e) {
      console.error('[recommendations] error getting cached uid', e);
      return null;
    }
  }

  function setCachedUid(uid) {
    try { localStorage.setItem(LS_UID_KEY, uid); } catch (e) {
      console.error('[recommendations] error setting cached uid', e);
    }
  }

  // ---------------------------------------------------------------------------
  // Card rendering via Shopify Section Rendering API
  // ---------------------------------------------------------------------------

  /**
   * Get locale URL prefix from a container's owning section (e.g. "" or "/en").
   * Shopify exposes this via request.locale.root_url; treat "/" as empty.
   */
  function getLocalePrefixForContainer(container) {
    if (!container) return '';
    var sectionEl = container.closest('[data-recs-section]');
    if (!sectionEl) return '';
    var root = sectionEl.getAttribute('data-locale-root') || '';
    return root === '/' ? '' : root;
  }

  /**
   * Returns true if the product is currently available (not sold out).
   * Uses the lightweight storefront JSON endpoint — no auth required,
   * CDN-cached but reflects real-time inventory changes.
   * Prefixed with locale so responses come from the current language context.
   */
  async function fetchAvailability(handle, localePrefix) {
    try {
      var prefix = localePrefix || '';
      var response = await fetch(prefix + '/products/' + encodeURIComponent(handle) + '.js');
      if (!response.ok) return false;
      var data = await response.json();
      return data.available === true;
    } catch {
      return false;
    }
  }

  /**
   * Render a single product card via the Section Rendering API.
   * Returns the raw HTML string for the card, or null on failure.
   * Prefixed with locale so rendered cards use the active language.
   */
  async function renderCardHtml(handle, localePrefix) {
    try {
      var prefix = localePrefix || '';
      var response = await fetch(
        prefix + '/products/' + encodeURIComponent(handle) + '?sections=' + SECTION_NAME
      );
      if (!response.ok) return null;
      var data = await response.json();
      return data[SECTION_NAME] || null;
    } catch {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Native bridge: signal when a recs container finishes rendering so the
  // MaiApp WebView host knows when to reveal the page (used by buyer-onboarding
  // to avoid revealing a blank /pages/recommendations during reload).
  // ---------------------------------------------------------------------------

  function postRecsRendered(count) {
    try {
      if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'mai-shop-recs-rendered',
          count: count || 0,
        }));
      }
    } catch (e) {
      // Best-effort signal; never throw into the page lifecycle.
    }
  }

  // ---------------------------------------------------------------------------
  // Per-container population
  // ---------------------------------------------------------------------------

  async function populateFallback(container, firebaseUser) {
    var sectionId = container.dataset.sectionId;
    var productsToShow = parseInt(container.dataset.productsToShow, 10) || 12;
    var isGridMode = container.dataset.gridMode === 'true';
    var spinner = document.getElementById('recs-spinner-' + sectionId);
    var list = document.getElementById('Slider-' + sectionId);
    var localePrefix = getLocalePrefixForContainer(container);

    try {
      // Try the cached UID immediately so we can start rendering without
      // waiting for Firebase auth to resolve on cache hits.
      var cachedUid = getCachedUid();
      var handles = cachedUid ? getLocalHandles(cachedUid) : null;

      // Kick off Firebase auth in parallel regardless — we need it to keep
      // the UID cache fresh and to fetch from the backend on cache misses.
      var userPromise = firebaseUser
        ? Promise.resolve(firebaseUser)
        : (window.firebaseAuth
            ? window.firebaseAuth.waitForAuthState(3000).catch(function () { return null; })
            : Promise.resolve(null));

      if (handles) {
        // Cache hit — no spinner needed (grid already shows Liquid skeletons)
        if (spinner) spinner.style.display = 'none';
        // Update the UID cache in the background after Firebase resolves
        userPromise.then(function (resolvedUser) {
          if (resolvedUser) setCachedUid(resolvedUser.uid);
        });
      } else {
        // Cache miss — must wait for Firebase auth before we can fetch
        var user = await userPromise;
        if (user) {
          setCachedUid(user.uid);
          handles = getLocalHandles(user.uid);
        }
        if (!handles) {
          var data = await fetchRecommendations();
          var recs = data.recommendations || {};
          handles = Array.isArray(recs.items)
            ? recs.items.filter(function (h) { return typeof h === 'string' && h; })
            : (recs.products || []).map(function (p) {
                if (p.handle) return p.handle;
                if (p.url) return p.url.replace(/^\/products\//, '').split('?')[0];
                return null;
              }).filter(Boolean);
          if (user && handles.length > 0) setLocalHandles(user.uid, handles);
        }
      }

      handles = (handles || []).slice(0, productsToShow);

      if (handles.length === 0) {
        if (spinner) spinner.style.display = 'none';
        // Remove Liquid-rendered skeletons (both grid and slider) and hide section
        if (list) list.querySelectorAll('.recs-skeleton-li').forEach(function (li) { li.remove(); });
        var sectionEl = document.querySelector('[data-recs-section="' + sectionId + '"]');
        if (sectionEl) sectionEl.style.display = 'none';
        postRecsRendered(0);
        return;
      }

      if (!list) {
        postRecsRendered(0);
        return;
      }

      if (isGridMode) {
        // Skeleton <li> elements are already in the DOM from the Liquid template
        // (one per products_to_show slot). Fill each slot as its card arrives,
        // or remove it for sold-out products. Unused tail slots are cleaned up
        // once all handles have resolved.
        if (spinner) spinner.style.display = 'none';
        var skeletons = Array.from(list.querySelectorAll('.recs-skeleton-li'));
        var remaining = handles.length;

        handles.forEach(function (handle, i) {
          var li = skeletons[i];
          if (!li) {
            // Safety fallback: more handles than pre-rendered slots (shouldn't happen)
            li = document.createElement('li');
            li.className = 'grid__item recs-skeleton-li';
            list.appendChild(li);
          }

          Promise.all([fetchAvailability(handle, localePrefix), renderCardHtml(handle, localePrefix)])
            .then(function (results) {
              remaining--;
              if (results[0] && results[1]) {
                li.className = 'grid__item';
                li.removeAttribute('aria-hidden');
                li.innerHTML = results[1];
              } else {
                li.remove();
              }
              if (remaining === 0) {
                // Remove any unused tail skeletons (handles.length < productsToShow)
                list.querySelectorAll('.recs-skeleton-li').forEach(function (s) { s.remove(); });

                var visibleCards = list.querySelectorAll('.card-wrapper[data-item-id]');

                // Fire Product List Viewed only on /pages/recommendations
                var recsPath = window.location.pathname.replace(/^\/[a-z]{2}(\/|$)/, '/');
                if (recsPath === '/pages/recommendations' && window.MaiAnalytics && visibleCards.length) {
                  var products = [];
                  visibleCards.forEach(function (card, idx) {
                    products.push(window.MaiAnalytics.getProductPropsFromCard(card));
                    products[idx].position = idx;
                  });
                  window.MaiAnalytics.track('Product List Viewed', {
                    list_id: 'recommendations',
                    category: 'recommendations',
                    products: products
                  });
                }
                postRecsRendered(visibleCards.length);
              }
            });
        });

      } else {
        // Slider mode: skeleton <li> elements are pre-rendered by Liquid.
        // Replace each slot in-place as its card arrives. Sold-out slots are
        // collected and removed together at the end to avoid mid-load layout
        // jumps. resetPages() recalculates slider offsets with the final items.
        if (spinner) spinner.style.display = 'none';
        skeletons = Array.from(list.querySelectorAll('.recs-skeleton-li'));
        remaining = handles.length;
        var toRemove = [];

        handles.forEach(function (handle, i) {
          var li = skeletons[i];
          if (!li) {
            li = document.createElement('li');
            li.className = 'grid__item slider__slide recs-skeleton-li';
            list.appendChild(li);
          }

          Promise.all([fetchAvailability(handle, localePrefix), renderCardHtml(handle, localePrefix)])
            .then(function (results) {
              remaining--;
              if (results[0] && results[1]) {
                li.className = 'grid__item slider__slide';
                li.removeAttribute('aria-hidden');
                li.innerHTML = results[1];
              } else {
                toRemove.push(li);
              }
              if (remaining === 0) {
                toRemove.forEach(function (el) { el.remove(); });
                list.querySelectorAll('.recs-skeleton-li').forEach(function (s) { s.remove(); });
                var sliderEl = document.getElementById('recs-slider-' + sectionId);
                if (sliderEl) {
                  if (typeof sliderEl.resetPages === 'function') sliderEl.resetPages();
                  var totalEl = sliderEl.querySelector('.slider-counter--total');
                  if (totalEl) totalEl.textContent = list.querySelectorAll('[id^="Slide-"]').length;
                }

                var recsCards = list.querySelectorAll('.card-wrapper[data-item-id]');

                // Fire Product List Viewed only on /pages/recommendations
                var recsSliderPath = window.location.pathname.replace(/^\/[a-z]{2}(\/|$)/, '/');
                if (recsSliderPath === '/pages/recommendations' && window.MaiAnalytics && recsCards.length) {
                  var recsProducts = [];
                  recsCards.forEach(function (card, idx) {
                    recsProducts.push(window.MaiAnalytics.getProductPropsFromCard(card));
                    recsProducts[idx].position = idx;
                  });
                  window.MaiAnalytics.track('Product List Viewed', {
                    list_id: 'recommendations',
                    category: 'recommendations',
                    products: recsProducts
                  });
                }
                postRecsRendered(recsCards.length);
              }
            });
        });
      }

    } catch {
      if (spinner) spinner.style.display = 'none';
      // Clean up stuck skeletons on error (both grid and slider mode)
      if (list) {
        list.querySelectorAll('.recs-skeleton-li').forEach(function (li) { li.remove(); });
      }
      postRecsRendered(0);
    }
  }

  // ---------------------------------------------------------------------------
  // Deferred fallback (index page: skeletons visible from first paint, JS
  // populates or hides after Firebase auth resolves)
  // ---------------------------------------------------------------------------


  /** Hide every deferred section — called when the user is not eligible. */
  function hideDeferredSections(containers) {
    containers.forEach(function (container) {
      var sectionEl = container.closest('[data-recs-section]');
      if (sectionEl) sectionEl.style.display = 'none';
    });
  }

  /**
   * Resolve Firebase auth and either populate the deferred sections or hide
   * them. Skeletons are already visible from the Liquid template; we only hide
   * in the ineligible / no-data cases.
   *
   * Two paths are needed because recommendations.js (defer) can run before
   * firebase-auth.js has set window.firebaseAuth:
   *   1. If window.firebaseAuth is already available: call waitForAuthState now.
   *   2. Listen for mai:auth:login-success in case Firebase isn't ready yet.
   * A timeout hides the section for users who are not logged in at all.
   */
  function initDeferredFallbacks(deferredContainers) {
    var resolved = false;

    function tryWithUser(user, source) {
      if (resolved) return;
      resolved = true;
      console.log('[recs] deferred auth resolved via "' + source + '" — user:', user ? (user.email || user.uid) : 'none');

      if (!user || !user.uid) {
        console.log('[recs] hiding — no user');
        hideDeferredSections(deferredContainers);
        postRecsRendered(0);
        return;
      }
      console.log('[recs] eligible — populating', deferredContainers.length, 'container(s)');
      deferredContainers.forEach(function (container) {
        populateFallback(container, user);
      });
    }

    console.log('[recs] initDeferredFallbacks — window.firebaseAuth available:', !!window.firebaseAuth);

    // Path 1: Firebase already initialised (cached session / fast load)
    if (window.firebaseAuth) {
      window.firebaseAuth.waitForAuthState(3000)
        .then(function (u) { tryWithUser(u, 'immediate'); })
        .catch(function () { tryWithUser(null, 'immediate-error'); });
    }

    // Path 2: Firebase-auth.js fires this event when auth state is ready
    document.addEventListener('mai:auth:login-success', function loginHandler() {
      document.removeEventListener('mai:auth:login-success', loginHandler);
      console.log('[recs] mai:auth:login-success received');
      var auth = window.firebaseAuth;
      if (auth) {
        auth.waitForAuthState(1000)
          .then(function (u) { tryWithUser(u, 'login-event'); })
          .catch(function () { tryWithUser(null, 'login-event-error'); });
      } else {
        tryWithUser(null, 'login-event-no-auth');
      }
    });

    // Path 3: Timeout — hide if nothing resolved (user is not logged in)
    setTimeout(function () {
      if (!resolved) tryWithUser(null, 'timeout-6s');
    }, 6000);
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  function init() {
    var containers = document.querySelectorAll('[data-needs-fallback]');
    var deferredContainers = [];

    containers.forEach(function (container) {
      var mode = container.getAttribute('data-needs-fallback');
      if (mode === 'deferred') {
        deferredContainers.push(container);
      } else {
        populateFallback(container);
      }
    });

    if (deferredContainers.length > 0) {
      initDeferredFallbacks(deferredContainers);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
